import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { IMPACT_STATS } from "@/data/content";
import { FadeUp } from "./Reveal";

const ACCENTS = ["text-jawa-coral", "text-jawa-turquoise", "text-jawa-yellow", "text-jawa-green"];

// Count-up for the numeric part of a value like "70+" or "36 + FCT"
const useCountUp = (target, start) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start || !target) return;
    let raf;
    const t0 = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return n;
};

const parseStat = (value) => {
  const m = String(value).match(/^(\d+)(.*)$/);
  return m ? { num: parseInt(m[1], 10), suffix: m[2] } : { num: 0, suffix: String(value) };
};

const Stat = ({ stat, accent, large, animate }) => {
  const { num, suffix } = parseStat(stat.value);
  const n = useCountUp(num, animate);
  return (
    <div className={`flex flex-col items-center text-center ${large ? "gap-3 px-6 py-10" : "gap-1 px-4 py-4"}`} data-testid={`impact-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
      <span className={`font-serif font-medium ${accent} ${large ? "text-5xl lg:text-6xl" : "text-2xl"}`}>
        {num ? n : ""}{suffix}
      </span>
      <span className={`uppercase tracking-[0.2em] text-jawa-ivory/60 ${large ? "text-xs" : "text-[10px]"}`}>{stat.label}</span>
    </div>
  );
};

export const ImpactStats = ({ variant = "full" }) => {
  const [stats, setStats] = useState(IMPACT_STATS);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/site/stats").then((r) => {
      if (Array.isArray(r.data?.stats) && r.data.stats.length === 4) setStats(r.data.stats);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (variant === "mini") {
    return (
      <div ref={ref} className="grid grid-cols-2 gap-px bg-jawa-ivory/10 sm:grid-cols-4" data-testid="impact-stats-mini">
        {stats.map((s, i) => (
          <div key={s.label} className="bg-jawa-nearblack">
            <Stat stat={s} accent={ACCENTS[i % 4]} animate={inView} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <section ref={ref} className="bg-jawa-nearblack" data-testid="impact-stats-section">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        <FadeUp>
          <p className="text-center text-xs uppercase tracking-[0.35em] text-jawa-turquoise">JÀWÀ in numbers</p>
        </FadeUp>
        <div className="mt-8 grid grid-cols-2 divide-jawa-ivory/10 lg:grid-cols-4 lg:divide-x">
          {stats.map((s, i) => (
            <Stat key={s.label} stat={s} accent={ACCENTS[i % 4]} large animate={inView} />
          ))}
        </div>
        <FadeUp>
          <p className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-jawa-ivory/35">
            Figures we can stand behind — updated as they grow.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};
